import {
  AttributeType,
  FootprintPolygonsType,
  LevelType,
  PositionType,
  SectionType,
  SpaceType
} from 'data/types';
import { produce } from 'immer';
import { markAutoLayoutSpace } from 'utils/auto-layout-space';
import { levelHasFullDuctwork } from 'utils/ductwork';
import { polygonBoundingBox } from 'utils/footprint';
import {
  getEffectiveFootprintPolygons,
  legacyFootprintToPolygons
} from 'utils/footprint-polygons';
import { isDuctedSystemSelected } from 'utils/project';
import { isRectangleBboxVertices } from 'utils/space-polygon';
import {
  createSection,
  createSpace,
  isLevelConditioned
} from 'utils/structure';

type AutoMirrorLevelLayoutArgs = {
  structure: LevelType[];
  projectSettings: AttributeType[];
  openSpaceItemId: number;
  footprint: PositionType[] | null;
};

function levelHasSpaces(level: LevelType): boolean {
  return level.sections.some((section) => section.spaces.length > 0);
}

function resolveLevelFootprints(
  level: LevelType,
  fallbackFootprint: PositionType[] | null
): FootprintPolygonsType {
  const levelPolygons = getEffectiveFootprintPolygons(level);
  return levelPolygons.length > 0
    ? levelPolygons
    : legacyFootprintToPolygons(fallbackFootprint);
}

export function needsAutoMirror(structure: LevelType[]): boolean {
  return structure.some(
    (level) =>
      (level.type === 'attic' ||
        level.type === 'basement' ||
        level.type === 'crawlspace') &&
      !isLevelConditioned(level) &&
      !levelHasSpaces(level)
  );
}

export type FootprintSpaceGeometry = {
  position: PositionType;
  width?: number;
  length?: number;
  vertices?: PositionType[];
  squareFootage: number;
};

export function footprintSpaceGeometry(
  footprintPoints: PositionType[]
): FootprintSpaceGeometry | null {
  const bb = polygonBoundingBox(footprintPoints);
  const width = bb.maxX - bb.minX;
  const height = bb.maxY - bb.minY;
  if (width <= 0 || height <= 0) return null;

  const relativeVertices = footprintPoints.map((p) => ({
    x: p.x - bb.minX,
    y: p.y - bb.minY
  }));
  const isRectangle = isRectangleBboxVertices(relativeVertices, width, height);
  return {
    position: { x: bb.minX, y: bb.minY },
    width: isRectangle ? width : undefined,
    length: isRectangle ? height : undefined,
    vertices: isRectangle ? undefined : relativeVertices,
    squareFootage: Math.ceil(width * height)
  };
}

export function applyFootprintGeometryToSpace(
  space: SpaceType,
  footprintPoints: PositionType[]
): SpaceType | null {
  const geometry = footprintSpaceGeometry(footprintPoints);
  if (!geometry) return null;
  return {
    ...space,
    position: geometry.position,
    width: geometry.width,
    length: geometry.length,
    vertices: geometry.vertices
  };
}

function createFootprintSpace(
  footprintPoints: PositionType[],
  targetLevel: LevelType,
  projectSettings: AttributeType[],
  openSpaceItemId: number,
  catalogAttributes: AttributeType[]
): SpaceType | null {
  const geometry = footprintSpaceGeometry(footprintPoints);
  if (!geometry) return null;

  const space = createSpace(openSpaceItemId, {
    attributesToInherit: [...projectSettings, ...targetLevel.attributes],
    data: {
      label: targetLevel.label,
      conditioned: isLevelConditioned(targetLevel),
      openTo: [],
      position: geometry.position,
      width: geometry.width,
      length: geometry.length,
      vertices: geometry.vertices
    }
  });

  if (!space) return null;
  const withSquareFootage = {
    ...space,
    attributes: space.attributes.map((attribute) =>
      attribute.code === 'square_footage'
        ? { ...attribute, value: geometry.squareFootage }
        : attribute
    )
  };
  return markAutoLayoutSpace(withSquareFootage, catalogAttributes);
}

function createFootprintSection(
  footprints: FootprintPolygonsType,
  targetLevel: LevelType,
  projectSettings: AttributeType[],
  openSpaceItemId: number,
  catalogAttributes: AttributeType[] = []
): SectionType[] {
  const spaces = footprints
    .map((footprintPoints) =>
      createFootprintSpace(
        footprintPoints,
        targetLevel,
        projectSettings,
        openSpaceItemId,
        catalogAttributes
      )
    )
    .filter((space): space is SpaceType => space !== null);
  if (spaces.length === 0) return [];
  return [createSection({ spaces })];
}

function createMirroredSections(
  sourceLevel: LevelType,
  targetLevel: LevelType,
  projectSettings: AttributeType[],
  openSpaceItemId: number
): SectionType[] {
  const sourceSpaces = sourceLevel.sections.flatMap(
    (section) => section.spaces
  );

  const sourceToMirroredId = new Map<string, string>();
  const mirroredSpaces = sourceSpaces
    .map((space) => {
      if (!space.position) {
        return null;
      }

      const hasRectShape = !!space.width && !!space.length;
      const hasPolygonShape = !!space.vertices && space.vertices.length >= 3;
      if (!hasRectShape && !hasPolygonShape) {
        return null;
      }

      const mirrored = createSpace(openSpaceItemId, {
        attributesToInherit: [...projectSettings, ...targetLevel.attributes],
        data: {
          label: targetLevel.label,
          conditioned: isLevelConditioned(targetLevel),
          openTo: [],
          position: {
            x: space.position.x,
            y: space.position.y
          },
          width: space.width,
          length: space.length,
          vertices: space.vertices?.map((v) => ({ x: v.x, y: v.y }))
        }
      });

      if (!mirrored) {
        return null;
      }

      sourceToMirroredId.set(space.uniqueId, mirrored.uniqueId);
      return { sourceSpace: space, mirrored };
    })
    .filter(
      (
        entry
      ): entry is {
        sourceSpace: (typeof sourceSpaces)[number];
        mirrored: NonNullable<ReturnType<typeof createSpace>>;
      } => entry !== null
    );

  if (mirroredSpaces.length === 0) {
    return [];
  }

  const mappedSpaces = mirroredSpaces.map(({ sourceSpace, mirrored }) => ({
    ...mirrored,
    openTo: (sourceSpace.openTo ?? [])
      .map((sourceId) => sourceToMirroredId.get(sourceId))
      .filter((id): id is string => id !== undefined)
  }));

  return [createSection({ spaces: mappedSpaces })];
}

function getSectionsForLevel(
  targetLevel: LevelType,
  sourceLevel: LevelType | null,
  projectSettings: AttributeType[],
  openSpaceItemId: number,
  footprint: PositionType[] | null
): SectionType[] {
  const effectiveFootprints = resolveLevelFootprints(targetLevel, footprint);
  if (effectiveFootprints.length > 0) {
    return createFootprintSection(
      effectiveFootprints,
      targetLevel,
      projectSettings,
      openSpaceItemId
    );
  }

  if (sourceLevel && levelHasSpaces(sourceLevel)) {
    return createMirroredSections(
      sourceLevel,
      targetLevel,
      projectSettings,
      openSpaceItemId
    );
  }

  return [];
}

type UnconditionedLevelTarget = {
  index: number;
  direction: 1 | -1;
};

function populateUnconditionedLevel(
  draft: LevelType[],
  target: UnconditionedLevelTarget,
  projectSettings: AttributeType[],
  openSpaceItemId: number,
  footprint: PositionType[] | null
): boolean {
  const level = draft[target.index];
  if (isLevelConditioned(level)) {
    return false;
  }
  if (levelHasSpaces(level)) {
    return false;
  }

  const sourceIndex = target.index + target.direction;
  const sourceLevel =
    sourceIndex >= 0 && sourceIndex < draft.length ? draft[sourceIndex] : null;

  const newSections = getSectionsForLevel(
    level,
    sourceLevel,
    projectSettings,
    openSpaceItemId,
    footprint
  );

  if (newSections.length === 0) {
    return false;
  }

  level.sections = newSections;
  return true;
}

function collectUnconditionedTargets(
  draft: LevelType[]
): UnconditionedLevelTarget[] {
  const targets: UnconditionedLevelTarget[] = [];
  const basementIndex = draft.findIndex((level) => level.type === 'basement');
  if (basementIndex !== -1) {
    targets.push({ index: basementIndex, direction: 1 });
  }
  const crawlspaceIndex = draft.findIndex(
    (level) => level.type === 'crawlspace'
  );
  if (crawlspaceIndex !== -1) {
    targets.push({ index: crawlspaceIndex, direction: 1 });
  }
  const atticIndex = draft.findIndex((level) => level.type === 'attic');
  if (atticIndex !== -1) {
    targets.push({ index: atticIndex, direction: -1 });
  }
  return targets;
}

export function autoMirrorUnconditionedLevelOpenSpaces({
  structure,
  projectSettings,
  openSpaceItemId,
  footprint
}: AutoMirrorLevelLayoutArgs): LevelType[] {
  return produce(structure, (draft) => {
    const targets = collectUnconditionedTargets(draft);

    let progressed = true;
    while (progressed) {
      progressed = false;
      for (const target of targets) {
        const populated = populateUnconditionedLevel(
          draft,
          target,
          projectSettings,
          openSpaceItemId,
          footprint
        );
        if (populated) {
          progressed = true;
        }
      }
    }
  });
}

type AutoGenerateFullDuctworkSpacesArgs = {
  structure: LevelType[];
  projectSettings: AttributeType[];
  customSpaceItemId: number;
  footprint: PositionType[] | null;
};

function getFullDuctworkFootprints(
  level: LevelType,
  footprint: PositionType[] | null,
  isDuctedSystem: boolean
): FootprintPolygonsType | null {
  if (!isDuctedSystem) {
    return null;
  }
  if (!levelHasFullDuctwork(level)) {
    return null;
  }
  if (levelHasSpaces(level)) {
    return null;
  }
  const effectiveFootprints = resolveLevelFootprints(level, footprint);
  return effectiveFootprints.length > 0 ? effectiveFootprints : null;
}

export function needsFullDuctworkSpaceGeneration(
  structure: LevelType[],
  footprint: PositionType[] | null,
  isDuctedSystem: boolean
): boolean {
  return structure.some(
    (level) =>
      getFullDuctworkFootprints(level, footprint, isDuctedSystem) !== null
  );
}

export function autoGenerateFullDuctworkLevelSpaces({
  structure,
  projectSettings,
  customSpaceItemId,
  footprint
}: AutoGenerateFullDuctworkSpacesArgs): LevelType[] {
  const isDuctedSystem = isDuctedSystemSelected(projectSettings);
  return produce(structure, (draft) => {
    draft.forEach((level) => {
      const effectiveFootprints = getFullDuctworkFootprints(
        level,
        footprint,
        isDuctedSystem
      );
      if (!effectiveFootprints) {
        return;
      }
      const newSections = createFootprintSection(
        effectiveFootprints,
        level,
        projectSettings,
        customSpaceItemId
      );
      if (newSections.length === 0) {
        return;
      }
      // eslint-disable-next-line no-param-reassign
      level.sections = newSections;
    });
  });
}

function getHybridFullLevelFootprints(
  level: LevelType,
  footprint: PositionType[] | null,
  isHybrid: boolean
): FootprintPolygonsType | null {
  if (!isHybrid) {
    return null;
  }
  if (levelHasSpaces(level)) {
    return null;
  }
  const effectiveFootprints = resolveLevelFootprints(level, footprint);
  return effectiveFootprints.length > 0 ? effectiveFootprints : null;
}

export function needsHybridFullLevelSpaceGeneration(
  structure: LevelType[],
  footprint: PositionType[] | null,
  isHybrid: boolean
): boolean {
  return structure.some(
    (level) => getHybridFullLevelFootprints(level, footprint, isHybrid) !== null
  );
}

type AutoGenerateHybridFullLevelSpacesArgs = {
  structure: LevelType[];
  projectSettings: AttributeType[];
  customSpaceItemId: number;
  openSpaceItemId: number;
  footprint: PositionType[] | null;
  catalogAttributes: AttributeType[];
  isHybrid: boolean;
};

export function collectNewSpaceIds(
  previous: LevelType[],
  next: LevelType[]
): string[] {
  const previousIds = new Set(
    previous.flatMap((level) =>
      level.sections.flatMap((section) =>
        section.spaces.map((space) => space.uniqueId)
      )
    )
  );
  return next.flatMap((level) =>
    level.sections.flatMap((section) =>
      section.spaces
        .map((space) => space.uniqueId)
        .filter((id) => !previousIds.has(id))
    )
  );
}

export function autoGenerateHybridFullLevelSpaces({
  structure,
  projectSettings,
  customSpaceItemId,
  openSpaceItemId,
  footprint,
  catalogAttributes,
  isHybrid
}: AutoGenerateHybridFullLevelSpacesArgs): LevelType[] {
  return produce(structure, (draft) => {
    draft.forEach((level) => {
      const effectiveFootprints = getHybridFullLevelFootprints(
        level,
        footprint,
        isHybrid
      );
      if (!effectiveFootprints) {
        return;
      }
      const spaceItemId = isLevelConditioned(level)
        ? customSpaceItemId
        : openSpaceItemId;
      const newSections = createFootprintSection(
        effectiveFootprints,
        level,
        projectSettings,
        spaceItemId,
        catalogAttributes
      );
      if (newSections.length === 0) {
        return;
      }
      // eslint-disable-next-line no-param-reassign
      level.sections = newSections;
    });
  });
}
